import type { SwimlaneLayoutDTO, TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import type { Item } from '@/lib/api/types/items';
import type { ItemTypeLevelOption, ItemTypeOption } from '@/lib/api/types/roadmapping';

/**
 * Filters items to only include those at the specified item level.
 *
 * This function replicates the swimlane's filtering logic to ensure items are filtered
 * consistently across the application (swimlane view, color panel, etc.).
 *
 * @param items - Array of all items to filter
 * @param itemTypes - Array of item types containing itemLevelId mappings
 * @param itemLevels - Array of item type levels
 * @param layout - Layout configuration containing the target itemLevelId
 * @returns Filtered array of items at the target level, or empty array if data is missing
 *
 * @example
 * const filteredItems = filterItemsByLevel(allItems, itemTypes, itemLevels, layout);
 */
export function filterItemsByLevel({
  items,
  itemTypes,
  itemLevels,
  layout,
}: {
  items: readonly Item[];
  itemTypes: ItemTypeOption[] | undefined;
  itemLevels: ItemTypeLevelOption[] | undefined;
  layout: SwimlaneLayoutDTO | TimelineLayoutDTO | null | undefined;
}): Item[] {
  if (!layout || !itemLevels || !itemTypes) {
    return [];
  }

  // Create a lookup map for efficient level access
  const levelsById = itemLevels.reduce((acc, level) => {
    acc.set(level.id, level);
    return acc;
  }, new Map<number, ItemTypeLevelOption>());

  // Determine the target level ID
  let levelId = layout.itemLevelId;
  if (!levelId) {
    // Fall back to the minimum level if not specified
    levelId = itemLevels.reduce((minLevel, currentLevel) =>
      currentLevel.level < minLevel.level ? currentLevel : minLevel,
    ).id;
  }

  const targetLevel = levelId ? levelsById.get(levelId) : undefined;
  if (!targetLevel) {
    return [];
  }

  // Get all item type IDs that belong to the target level
  const targetItemTypeIds = itemTypes
    .filter((type) => type.itemLevelId === targetLevel.id)
    .map((type) => type.id);
  const targetItemTypeIdsSet = new Set(targetItemTypeIds);

  // Filter items to only those with matching item type IDs
  return items.filter((item) => targetItemTypeIdsSet.has(item.itemTypeId));
}
